import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';

import { AuthProvider } from '@/auth/auth-provider';
import { RequireAuth, RequireGuest } from '@/auth/require-auth';
import { ApiClientProvider } from '@/components/layout/api-client-provider';
import { PublicLayout } from '@/components/layout/public-layout';
import ForgotPasswordPage from '@/pages/auth/forgot-password-page';
import LoginPage from '@/pages/auth/login-page';
import LoginTokenPage from '@/pages/auth/login-token-page';
import RegisterPage from '@/pages/auth/register-page';
import ResetPasswordPage from '@/pages/auth/reset-password-page';
import SettingsPage from '@/pages/auth/settings-page';
import VerifyEmailPage from '@/pages/auth/verify-email-page';
import GamePage from '@/pages/game-page';
import GamesPage from '@/pages/games-page';
import HomePage from '@/pages/home-page';
import IpLookupPage from '@/pages/ip-lookup-page';
import LeaderboardPage from '@/pages/leaderboard-page';
import LegalPage from '@/pages/legal-page';
import NotFoundPage from '@/pages/not-found-page';
import PlayerProfilePage from '@/pages/player-profile-page';
import SeasonsPage from '@/pages/seasons-page';
import ServerPage from '@/pages/server-page';
import ServersPage from '@/pages/servers-page';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function AppRoutes() {
  return (
    <Routes>
      {/* Public URLs are kept 1:1 with the legacy site (search index, inbound
          links). Legacy routes without an equivalent screen redirect to the
          closest page rather than 404. */}
      <Route element={<PublicLayout />}>
        <Route index element={<HomePage />} />
        <Route path="server" element={<ServersPage />} />
        <Route path="server/:id" element={<ServerPage />} />
        <Route path="game" element={<GamesPage />} />
        <Route path="game/:id" element={<GamePage />} />
        <Route path="player/top" element={<LeaderboardPage />} />
        <Route path="player/:name" element={<PlayerProfilePage />} />
        <Route path="season" element={<SeasonsPage />} />
        <Route path="season/:id" element={<LeaderboardPage />} />
        <Route path="season/:id/server/:server" element={<LeaderboardPage />} />
        {/* Legacy public account pages: accounts are private now; players are the public identity. */}
        <Route path="user/:id" element={<Navigate to="/" replace />} />
        <Route path="ip" element={<IpLookupPage />} />
        <Route path="ip/:ip" element={<IpLookupPage />} />
        <Route path="asn/:ip" element={<IpLookupPage />} />
        <Route path="legal" element={<LegalPage />} />

        {/* Email-verify link lands here whether or not the tab is logged in.
            Pre-cutover links (`/email/verify/{id}?expires=&signature=`) render
            the page in place (URL + query intact) - it shows the
            "request a new link" hint, as the new backend can't validate them. */}
        <Route path="email/verify" element={<VerifyEmailPage />} />
        <Route path="email/verify/:id" element={<VerifyEmailPage />} />
        <Route path="email/resend" element={<Navigate to="/email/verify" replace />} />

        <Route element={<RequireGuest />}>
          <Route path="login" element={<LoginPage />} />
          <Route path="register" element={<RegisterPage />} />
          <Route path="forgot" element={<ForgotPasswordPage />} />
          <Route path="reset" element={<ResetPasswordPage />} />
        </Route>

        <Route element={<RequireAuth />}>
          <Route path="settings" element={<SettingsPage />} />
          <Route path="settings/not_verified" element={<Navigate to="/email/verify" replace />} />
          <Route path="token" element={<LoginTokenPage />} />
        </Route>

        <Route path="*" element={<NotFoundPage />} />
      </Route>
    </Routes>
  );
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <ApiClientProvider>
            <AppRoutes />
          </ApiClientProvider>
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
